import { NavLink } from 'react-router-dom'
import { FolderOpen, Calendar, User } from 'lucide-react'
import { useI18n } from '../../i18n'
import orellaIconBlue from '../../assets/icons/orella_icon_blue.png'
import { TAB_ROUTES } from '../../utils/tabRoutes'

// Custom Orella Icon component for Home tab
const OrellaIcon = ({ isActive }) => (
  <img 
    src={orellaIconBlue} 
    alt="Home" 
    className={`w-6 h-6 transition-opacity ${isActive ? 'opacity-100' : 'opacity-40'}`}
    style={{ filter: isActive ? 'none' : 'grayscale(100%)' }}
  />
)

// Label and icon for each bottom tab, KEYED BY PATH. TAB_ROUTES
// (utils/tabRoutes.js) is the shared source of the PATHS and their order, so this
// file and WhatsNewSheet's allowlist can never disagree about what a "tab route" is.
// PROD-352: this was a positional array paired by index, so reordering either list
// gave a tab another tab's label and icon. A lookup by path has no index to disagree on.
const TAB_META = {
  '/home': { labelKey: 'nav.home', icon: null, isOrellaIcon: true },
  '/cases': { labelKey: 'nav.cases', icon: FolderOpen },
  '/visits': { labelKey: 'nav.visits', icon: Calendar },
  '/profile': { labelKey: 'nav.profile', icon: User },
}

export default function TabBar() {
  const { t } = useI18n()

  const tabs = TAB_ROUTES.map((path) => ({ path, ...TAB_META[path] }))

  // v242.80.0 (TF-37) — reported four times, and TF-06 already "fixed" it once by
  // shrinking the row from h-20 to h-16. That was the wrong lever, which is why
  // it came back.
  //
  // The empty white band is not the row's height. It is
  // env(safe-area-inset-bottom) — about 34px on a device with a home indicator —
  // reserved BELOW the labels as bare white. Shrinking the row moves the labels
  // UP and leaves the same band untouched, making the gap proportionally worse.
  //
  // The inset exists so content clears the home indicator, but the indicator is
  // only ~5px of glyph sitting in the middle of that band. Reserving the full
  // inset as blank white is more clearance than it needs. Reserving LESS moves
  // the whole row down toward the physical edge while still clearing it, which
  // is what the report asks for. max(..., 0px) so a device with no inset is
  // unaffected.
  return (
    <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-3xl bg-white border-t border-gray-100 z-50 hamburger-hides-me"
         style={{ paddingBottom: 'max(calc(env(safe-area-inset-bottom, 0px) - 14px), 0px)' }}>
      {/* ★ COUPLED TO MobileLayout's reserved paddingBottom. If this row's height
          or the inset expression changes, that one has to change with it or the
          last item on every page hides behind the bar. */}
      <div className="flex justify-around items-center h-16 px-4">
        {tabs.map(({ path, labelKey, icon: Icon, isOrellaIcon }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) => `
              flex flex-col items-center gap-1 py-1 px-4 rounded-xl transition-colors
              ${isActive ? 'text-orella-500' : 'text-gray-400 hover:text-gray-600'}
            `}
          >
            {({ isActive }) => (
              <>
                {isOrellaIcon ? (
                  <OrellaIcon isActive={isActive} />
                ) : (
                  <Icon size={24} />
                )}
                <span className="text-xs font-medium">{t(labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}
